import React, { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { Alert, Col, Empty, Row, Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS.jsx';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import PhdHelpHint from '../../../../common/Phd/PhdHelpHint.jsx';
import { MainTitle, Spinner } from '../../../../common/index.jsx';
import CollapseBlock from '../../../../common/blocks/CollapseBlock/CollapseBlock.jsx';
import HtmlContentWithAntdImages from '../../../../common/Image/HtmlContentWithAntdImages.jsx';
import usePhdStudentProfile from '../usePhdStudentProfile.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient';
import {
  useGetPhdIndividualPlanSubjectsQuery,
  useGetPhdIndividualPlanTasksQuery,
  useGetPhdStudySubjectsQuery,
} from '../../../../../redux/services/phd.jsx';
import { showErrorPage } from '../../../../../redux/slices/coreSlice.jsx';

const formatDate = (value) => (value ? dayjs(value).format('DD.MM.YYYY') : '—');
const hasHtmlTags = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ''));

const PhdStudentPlanPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    isLoggedIn,
    data: studentProfile,
    isFetching,
    error,
  } = usePhdStudentProfile();

  const pageTitle = t('Môj individuálny plán');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  // Clear global error overlay if backend returns 404 for missing profile
  useEffect(() => {
    dispatch(showErrorPage(undefined));
  }, [dispatch, studentProfile, error]);

  const plans = studentProfile?.individual_plans || [];
  const plan = useMemo(() => {
    if (!plans.length) {
      return null;
    }

    return [...plans].sort(
      (a, b) =>
        Number(Boolean(b.is_canonical)) - Number(Boolean(a.is_canonical)) ||
        dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf(),
    )[0];
  }, [plans]);

  const planId = plan?.id;
  const { data: planTasksData, isFetching: isLoadingPlanTasks } =
    useGetPhdIndividualPlanTasksQuery(
      planId ? { phd_individual_plan_id: planId } : undefined,
      { skip: !planId },
    );

  const { data: planSubjectsData, isFetching: isLoadingPlanSubjects } =
    useGetPhdIndividualPlanSubjectsQuery(
      planId ? { phd_individual_plan_id: planId } : undefined,
      {
        skip: !planId,
      },
    );

  const { data: studySubjects, isFetching: isLoadingStudySubjects } =
    useGetPhdStudySubjectsQuery(undefined, { skip: !planId });

  const studySubjectMap = useMemo(
    () =>
      new Map((studySubjects || []).map((subject) => [subject.id, subject])),
    [studySubjects],
  );

  const subjectLabel = (id) => {
    const subject = studySubjectMap.get(id);
    if (!subject) {
      return '—';
    }

    const identity = subject.code
      ? `${subject.code} – ${subject.name}`
      : subject.name;
    const completionTypeLabels = {
      S: t('Skúška'),
      T: t('Štátna skúška'),
      V: t('Absolvovanie'),
    };

    return `${identity} · ${completionTypeLabels[subject.completion_type] || subject.completion_type}`;
  };

  const displayedTasks = useMemo(() => {
    if (planTasksData?.length) {
      return (planTasksData || []).filter(
        (task) => task?.phd_individual_plan_id === planId,
      );
    }

    return plan?.tasks || [];
  }, [plan?.tasks, planId, planTasksData]);

  const planSubjects = useMemo(
    () =>
      (planSubjectsData || []).filter(
        (row) => row?.phd_individual_plan_id === planId,
      ),
    [planId, planSubjectsData],
  );

  const displayedSubjects = useMemo(() => {
    if (planSubjects.length) {
      return planSubjects;
    }

    return (plan?.subjects || []).map((subject) => ({
      id: subject?.pivot?.id || subject.id,
      phd_study_subject_id: subject.id,
      grade: subject?.pivot?.grade,
    }));
  }, [plan?.subjects, planSubjects]);

  const isNestedLoading =
    isLoadingPlanTasks || isLoadingPlanSubjects || isLoadingStudySubjects;

  const taskColumns = useMemo(
    () => [
      {
        title: t('Úloha'),
        dataIndex: 'task',
        key: 'task',
        render: (value) => value || '—',
      },
      {
        title: (
          <span>
            {t('Termín')}
            <PhdHelpHint
              label={t('Vysvetlenie termínu úlohy')}
              title={t(
                'Termín úlohy je informačný a neovplyvňuje postup v doktorandskom štúdiu.',
              )}
            />
          </span>
        ),
        dataIndex: 'date_deadline',
        key: 'date_deadline',
        render: (value) => (value ? formatDate(value) : '—'),
      },
    ],
    [t],
  );

  const subjectColumns = useMemo(
    () => [
      {
        title: t('Predmet'),
        dataIndex: 'phd_study_subject_id',
        key: 'phd_study_subject_id',
        render: (id) => subjectLabel(id),
      },
      {
        title: t('Hodnotenie'),
        dataIndex: 'grade',
        key: 'grade',
        render: (grade) => (grade ? <Tag color="blue">{t(grade)}</Tag> : '—'),
      },
    ],
    [t, studySubjectMap],
  );

  const renderRichTextValue = (value, fallback = '—') => {
    if (value === null || value === undefined) {
      return fallback;
    }

    const content = String(value).trim();
    if (!content) {
      return fallback;
    }

    if (hasHtmlTags(content)) {
      return <HtmlContentWithAntdImages content={content} />;
    }

    return (
      <p
        style={{
          whiteSpace: 'pre-line',
          wordBreak: 'break-word',
          marginBottom: 0,
        }}
      >
        {content}
      </p>
    );
  };

  const studyFormLabel = useMemo(() => {
    if (!studentProfile?.study_form) {
      return '—';
    }

    return t(studentProfile.study_form);
  }, [studentProfile?.study_form, t]);

  const profileStudyField =
    studentProfile?.study_field || studentProfile?.studyField;
  const profileStudyFieldLabel = profileStudyField
    ? profileStudyField.focus
      ? `${profileStudyField.study_field} - ${profileStudyField.focus}`
      : profileStudyField.study_field
    : undefined;
  const planStudyFieldLabel =
    [plan?.student_study_program, plan?.student_study_field]
      .filter(Boolean)
      .join(' - ') ||
    plan?.student_study_field ||
    plan?.student_study_program;
  const studyFieldLabel = profileStudyFieldLabel || planStudyFieldLabel || '—';

  const renderContent = () => {
    if (!isLoggedIn) {
      return (
        <CollapseBlock>
          <MainTitle title={t('Môj individuálny plán')} />
          <Alert
            type="warning"
            showIcon
            message={t('Pre zobrazenie individuálneho plánu sa prihláste.')}
          />
        </CollapseBlock>
      );
    }

    if (isFetching) {
      return <Spinner />;
    }

    if (!studentProfile) {
      const isNotFound = error?.status === 404;
      return (
        <CollapseBlock>
          <MainTitle title={t('Môj individuálny plán')} />
          <Alert
            type="info"
            showIcon
            message={
              isNotFound
                ? t('PhD profil pre tento účet zatiaľ neexistuje.')
                : error?.data?.message || t('PhD profil sa nenašiel.')
            }
          />
        </CollapseBlock>
      );
    }

    return (
      <CollapseBlock>
        <MainTitle title={t('Môj individuálny plán')} />

        {studentProfile.state === 'Interrupted' ? (
          <Alert
            type="warning"
            showIcon
            message={t('Štúdium je prerušené')}
            description={t(
              'Budúce študijné termíny sú pozastavené. Úlohy a predmety zostávajú viditeľné; zmenu stavu eviduje administrátor doktorandského štúdia.',
            )}
            style={{ marginBottom: 16 }}
          />
        ) : null}
        {['Finished', 'Expelled'].includes(studentProfile.state) ? (
          <Alert
            type="info"
            showIcon
            message={t('Zobrazený plán je historický a iba na čítanie')}
            description={t(
              'Po ukončení štúdia nemožno záznamy o jeho priebehu priamo meniť. Pri chybe kontaktujte administrátora doktorandského štúdia.',
            )}
            style={{ marginBottom: 16 }}
          />
        ) : null}

        <CollapseBlock title={t('Základné údaje')}>
          <Row gutter={16}>
            <Col span={12}>
              <strong>{t('Študent')}</strong>
              <p>{studentProfile.person?.full_name || '—'}</p>
            </Col>
            <Col span={12}>
              <strong>{t('Študijný odbor')}</strong>
              <p>{studyFieldLabel}</p>
            </Col>
            <Col span={12}>
              <strong>{t('Forma štúdia')}</strong>
              <p>{studyFormLabel}</p>
            </Col>
            <Col span={12}>
              <strong>{t('Stav štúdia')}</strong>
              <p>{studentProfile.state ? t(studentProfile.state) : '—'}</p>
            </Col>
            <Col span={24}>
              <strong>{t('Adresa')}</strong>
              <p>
                {studentProfile.student_full_address ||
                  plan?.student_full_address ||
                  '—'}
              </p>
            </Col>
          </Row>
        </CollapseBlock>

        <CollapseBlock title={t('Aktuálny individuálny plán')}>
          {plan ? (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <strong>{t('Školský rok')}</strong>
                  <p>
                    {plan.school_year
                      ? `${dayjs(plan.school_year.start_year).format('YYYY')}/${dayjs(plan.school_year.end_year).format('YYYY')}`
                      : '—'}
                  </p>
                </Col>
                <Col span={12}>
                  <strong>{t('Názov témy')}</strong>
                  <p>{plan.thesis_title || '—'}</p>
                </Col>
                <Col span={12}>
                  <strong>{t('Začiatok štúdia')}</strong>
                  <p>{formatDate(plan.date_study_start)}</p>
                </Col>
                <Col span={12}>
                  <strong>{t('Koniec štúdia')}</strong>
                  <p>{formatDate(plan.date_study_end)}</p>
                </Col>
                <Col span={12}>
                  <strong>{t('Dátum prihlášky')}</strong>
                  <p>{formatDate(plan.date_dissertation_application)}</p>
                </Col>
                <Col span={12}>
                  <strong>{t('Dátum odovzdania')}</strong>
                  <p>{formatDate(plan.date_dissertation_submission)}</p>
                </Col>
                <Col span={12}>
                  <strong>{t('Školiteľ')}</strong>
                  <p>{plan.supervisor_display_name || '—'}</p>
                </Col>
              </Row>
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 12 }}
                message={`${t('Úlohy v pláne')}: ${displayedTasks?.length || 0} | ${t(
                  'Predmety individuálneho plánu',
                )}: ${displayedSubjects?.length || 0}`}
              />
            </>
          ) : (
            <Empty
              description={t(
                'Individuálny plán zatiaľ neexistuje. Kontaktujte školiteľa alebo administrátora doktorandského štúdia; plán možno vytvoriť až po priradení účinnej dizertačnej témy.',
              )}
            />
          )}
        </CollapseBlock>

        {plan && (
          <CollapseBlock title={t('Tézy a sylaby')}>
            <Row gutter={16}>
              <Col span={24} style={{ marginBottom: 12 }}>
                <strong>{t('Tematické oblasti')}</strong>
                {renderRichTextValue(plan.thematic_areas)}
              </Col>
              <Col span={24} style={{ marginBottom: 12 }}>
                <strong>{t('Povinná literatúra')}</strong>
                {renderRichTextValue(plan.thesis_required_literature)}
              </Col>
              <Col span={24} style={{ marginBottom: 12 }}>
                <strong>{t('Odporúčaná literatúra')}</strong>
                {renderRichTextValue(plan.thesis_recommended_literature)}
              </Col>
              <Col span={24}>
                <strong>{t('Odporúčané prednášky')}</strong>
                {renderRichTextValue(plan.thesis_recommended_lectures)}
              </Col>
            </Row>
          </CollapseBlock>
        )}

        <CollapseBlock title={t('Úlohy v pláne')}>
          {isNestedLoading ? (
            <Spinner />
          ) : displayedTasks?.length ? (
            <Table
              dataSource={displayedTasks}
              columns={taskColumns}
              pagination={false}
              rowKey={(row) => row.id}
              size="small"
            />
          ) : (
            <Empty description={t('Úlohy zatiaľ nie sú definované.')} />
          )}
        </CollapseBlock>

        <CollapseBlock title={t('Predmety individuálneho plánu')}>
          {isNestedLoading ? (
            <Spinner />
          ) : displayedSubjects?.length ? (
            <Table
              dataSource={displayedSubjects}
              columns={subjectColumns}
              pagination={false}
              rowKey={(row) => row.id}
              size="small"
            />
          ) : (
            <Empty description={t('Predmety zatiaľ nie sú priradené.')} />
          )}
        </CollapseBlock>
      </CollapseBlock>
    );
  };

  return (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako pracovať s mojím študijným plánom?')}
          description={t(
            'Tu vidíte svoj plán, predmety, úlohy a známky. Splnené úlohy označte priebežne; pri chýbajúcom alebo historickom pláne kontaktujte administrátora doktorandského štúdia.',
          )}
        />
        {renderContent()}
      </div>
    </>
  );
};

export default PhdStudentPlanPage;
